import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import { Star, ArrowLeft, Send, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATING_CRITERIA = [
  { id: "rating_clarity", label: "Verständlichkeit", description: "Wie verständlich war der Kursinhalt?" },
  { id: "rating_practical", label: "Praxisbezug", description: "Wie praxisnah war der Inhalt?" },
  { id: "rating_quality", label: "Unterrichtsqualität", description: "Wie war die Qualität des Unterrichts?" },
  { id: "rating_materials", label: "Materialqualität", description: "Wie war die Qualität der Lernmaterialien?" },
  { id: "rating_pace", label: "Lerntempo", description: "Wie angemessen war das Tempo?" }
];

const StarRating = ({ value, onChange, disabled }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          className="focus:outline-none disabled:cursor-not-allowed"
          onClick={() => onChange?.(star)}
          onMouseEnter={() => !disabled && setHover(star)}
          onMouseLeave={() => setHover(0)}
        >
          <Star
            className={`h-6 w-6 transition-colors ${
              star <= (hover || value)
                ? "fill-yellow-400 text-yellow-400"
                : "text-muted-foreground"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const CourseFeedback = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form
  const [form, setForm] = useState({
    rating_overall: 0,
    rating_clarity: 0,
    rating_practical: 0,
    rating_quality: 0,
    rating_materials: 0,
    rating_pace: 0,
    comment: "",
    is_anonymous: false
  });

  useEffect(() => {
    loadData();
  }, [courseId]);

  const loadData = async () => {
    try {
      const [courseRes, feedbackRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`),
        axios.get(`${API}/feedback/course/${courseId}`)
      ]);
      
      setCourse(courseRes.data);
      setFeedbackStats(feedbackRes.data.stats);
      
      // Check if user already submitted feedback
      const userFeedback = feedbackRes.data.feedback.find(f => f.user_id === user?.id);
      if (userFeedback) {
        setExistingFeedback(userFeedback);
      }
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (form.rating_overall === 0) {
      toast.error("Bitte geben Sie eine Gesamtbewertung ab");
      return;
    }
    
    setSubmitting(true);
    try {
      const params = {
        rating_overall: form.rating_overall,
        comment: form.comment,
        is_anonymous: form.is_anonymous
      };
      
      // Add optional ratings if set
      RATING_CRITERIA.forEach(c => {
        if (form[c.id] > 0) {
          params[c.id] = form[c.id];
        }
      });
      
      await axios.post(`${API}/feedback/course/${courseId}`, null, { params });
      toast.success("Feedback gesendet!");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <ParticipantLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </ParticipantLayout>
    );
  }

  return (
    <ParticipantLayout>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="feedback-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Kursbewertung</h1>
            <p className="text-muted-foreground">{course?.title}</p>
          </div>
        </div>

        {/* Stats Card */}
        {feedbackStats && feedbackStats.total_reviews > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base">Aktuelle Bewertungen</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-primary">{feedbackStats.average_rating}</p>
                  <div className="flex justify-center my-1">
                    {[1,2,3,4,5].map(star => (
                      <Star 
                        key={star} 
                        className={`h-4 w-4 ${star <= Math.round(feedbackStats.average_rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">{feedbackStats.total_reviews} Bewertungen</p>
                </div>
                <div className="flex-1 space-y-2">
                  {feedbackStats.average_clarity > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-28">Verständlichkeit</span>
                      <Progress value={feedbackStats.average_clarity * 20} className="h-2 flex-1" />
                      <span className="text-xs w-8">{feedbackStats.average_clarity.toFixed(1)}</span>
                    </div>
                  )}
                  {feedbackStats.average_practical > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-28">Praxisbezug</span>
                      <Progress value={feedbackStats.average_practical * 20} className="h-2 flex-1" />
                      <span className="text-xs w-8">{feedbackStats.average_practical.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Already Submitted */}
        {existingFeedback ? (
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="py-8 text-center">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <h3 className="text-lg font-medium mb-2">Feedback bereits gesendet</h3>
              <p className="text-muted-foreground mb-4">
                Sie haben diesen Kurs am {new Date(existingFeedback.created_at).toLocaleDateString("de-DE")} bewertet.
              </p>
              <div className="flex justify-center">
                <StarRating value={existingFeedback.rating_overall} disabled />
              </div>
              {existingFeedback.comment && (
                <p className="mt-4 text-sm text-muted-foreground italic">"{existingFeedback.comment}"</p>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Feedback Form */
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Ihre Bewertung</CardTitle>
              <CardDescription>Helfen Sie uns, unsere Kurse zu verbessern.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Overall Rating */}
              <div className="space-y-2">
                <Label className="text-base font-medium">Gesamtbewertung *</Label>
                <StarRating 
                  value={form.rating_overall} 
                  onChange={(v) => setForm({ ...form, rating_overall: v })}
                />
              </div>

              {/* Detailed Ratings */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Detailbewertung (optional)</Label>
                {RATING_CRITERIA.map(criterion => (
                  <div key={criterion.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{criterion.label}</p>
                      <p className="text-xs text-muted-foreground">{criterion.description}</p>
                    </div>
                    <StarRating
                      value={form[criterion.id]}
                      onChange={(v) => setForm({ ...form, [criterion.id]: v })}
                    />
                  </div>
                ))}
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <Label>Kommentar (optional)</Label>
                <Textarea
                  value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  placeholder="Was hat Ihnen gefallen? Was könnte verbessert werden?"
                  rows={4}
                />
              </div>

              {/* Anonymous */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="anonymous"
                  checked={form.is_anonymous}
                  onCheckedChange={(checked) => setForm({ ...form, is_anonymous: checked })}
                />
                <Label htmlFor="anonymous" className="text-sm cursor-pointer">
                  Anonym bewerten (Ihr Name wird nicht angezeigt)
                </Label>
              </div>

              {/* Submit */}
              <Button 
                className="w-full" 
                onClick={handleSubmit} 
                disabled={submitting || form.rating_overall === 0}
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Feedback senden
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </ParticipantLayout>
  );
};

export default CourseFeedback;
